import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Building2, BookOpen, MapPin, Edit2, X, Check, Scale, Search, User, Eye, PenLine } from 'lucide-react';

const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

/* Render a single field input/select/textarea */
function FieldInput({ f, value, onChange }) {
  if (f.type === 'textarea') {
    return (
      <textarea
        className={inputCls}
        rows={2}
        placeholder={f.placeholder || f.label}
        value={value || ''}
        onChange={e => onChange(f.key, e.target.value)}
      />
    );
  }
  if (f.type === 'select') {
    return (
      <select
        className={inputCls}
        value={value || ''}
        onChange={e => onChange(f.key, e.target.value)}
        required={f.required}
      >
        <option value="">-- Select {f.label} --</option>
        {(f.options || []).map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      className={inputCls}
      placeholder={f.label}
      value={value || ''}
      onChange={e => onChange(f.key, e.target.value)}
      required={f.required}
    />
  );
}

/* Build a Google Maps embed URL from a court item.
   Uses address (most specific) so the map pins exactly one location.
   Falls back to name + location when no address is stored. */
function buildEmbedUrl(item) {
  const query = item.address
    ? encodeURIComponent(item.address)
    : encodeURIComponent([item.name, item.location].filter(Boolean).join(', Pune, Maharashtra'));
  return `https://maps.google.com/maps?q=${query}&output=embed&hl=en&z=16`;
}

/* Full-screen map modal */
function MapModal({ item, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-blue-700" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
              {item.address && <p className="text-xs text-gray-500 mt-0.5">{item.address}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={
                item.map_url ||
                `https://www.google.com/maps/search/${encodeURIComponent(
                  item.address
                    ? item.address
                    : [item.name, item.location, 'Pune, Maharashtra'].filter(Boolean).join(', ')
                )}`
              }
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50"
            >
              Open in Google Maps ↗
            </a>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
              <X size={20} />
            </button>
          </div>
        </div>
        {/* Map iframe */}
        <div className="flex-1" style={{ minHeight: '420px' }}>
          <iframe
            title={`Map - ${item.name}`}
            src={buildEmbedUrl(item)}
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: '420px' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}

function MasterSection({ title, icon: Icon, endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [search, setSearch] = useState('');
  const [mapItem, setMapItem] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(endpoint); setItems(data.results || data); } catch {}
  };

  useEffect(() => { load(); }, []);

  const setFormField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setEditField = (k, v) => setEditData(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoint, form);
      toast.success('Added successfully!');
      setForm({});
      setShowForm(false);
      load();
    } catch { toast.error('Failed to add'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`${endpoint}${id}/`); toast.success('Deleted!'); load(); } catch { toast.error('Failed'); }
  };

  const startEdit = (item) => { setEditId(item.id); setEditData({ ...item }); };
  const cancelEdit = () => { setEditId(null); setEditData({}); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`${endpoint}${id}/`, editData);
      toast.success('Updated!');
      setEditId(null);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const inlineFields = fields.filter(f => f.type !== 'textarea');
  const textareaFields = fields.filter(f => f.type === 'textarea');

  const filtered = search.trim()
    ? items.filter(item =>
        (item.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (item.designation || '').toLowerCase().includes(search.toLowerCase()) ||
        (item.court_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : items;

  return (
    <>
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-blue-900" />
          <h2 className="font-semibold text-gray-800">{title}</h2>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {search.trim() ? `${filtered.length} / ${items.length}` : items.length}
          </span>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-sm text-blue-900 hover:text-blue-700 font-medium">
          <Plus size={15} /> Add New
        </button>
      </div>

      {/* Search bar */}
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-blue-50 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            {inlineFields.map(f => (
              <div key={f.key} className={f.wide ? 'flex-1 min-w-[200px]' : 'w-44'}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <FieldInput f={f} value={form[f.key]} onChange={setFormField} />
              </div>
            ))}
          </div>
          {textareaFields.map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
              <FieldInput f={f} value={form[f.key]} onChange={setFormField} />
            </div>
          ))}
          <div className="flex gap-2">
            <button type="submit" disabled={loading} className="bg-blue-950 text-white px-4 py-2 rounded-xl text-sm font-medium">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="border px-4 py-2 rounded-xl text-sm text-gray-600">Cancel</button>
          </div>
        </form>
      )}

      <div className="divide-y divide-gray-50 overflow-y-auto max-h-[560px]">
        {filtered.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">
            {search.trim() ? 'No results found' : 'No entries yet'}
          </p>
        ) : filtered.map(item => (
          <div key={item.id} className="px-5 py-3">
            {editId === item.id ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {inlineFields.map(f => (
                    <div key={f.key} className={f.wide ? 'flex-1 min-w-[200px]' : 'w-44'}>
                      <FieldInput f={f} value={editData[f.key]} onChange={setEditField} />
                    </div>
                  ))}
                </div>
                {textareaFields.map(f => (
                  <FieldInput key={f.key} f={f} value={editData[f.key]} onChange={setEditField} />
                ))}
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(item.id)} className="flex items-center gap-1 text-xs bg-blue-950 text-white px-3 py-1.5 rounded-lg"><Check size={12} /> Save</button>
                  <button onClick={cancelEdit} className="flex items-center gap-1 text-xs border px-3 py-1.5 rounded-lg text-gray-600"><X size={12} /> Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{item.name}</p>
                  {item.designation && <p className="text-xs text-blue-700 font-medium">{item.designation}</p>}
                  {item.court_name && <p className="text-xs text-gray-500">📍 {item.court_name}</p>}
                  {item.location && <p className="text-xs text-gray-400">{item.location}</p>}
                  {item.address && <p className="text-xs text-gray-500 mt-0.5">{item.address}</p>}
                  {(item.map_url || item.address || item.location) && (
                    <button
                      onClick={() => setMapItem(item)}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium"
                    >
                      <MapPin size={11} /> View on Map
                    </button>
                  )}
                  {item.phone && <p className="text-xs text-gray-400 mt-0.5">{item.phone} {item.email ? `· ${item.email}` : ''}</p>}
                  {item.bar_council_number && <p className="text-xs text-gray-400">Bar No: {item.bar_council_number}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(item)} className="text-gray-300 hover:text-blue-500 transition-colors"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Map modal */}
    {mapItem && <MapModal item={mapItem} onClose={() => setMapItem(null)} />}
    </>
  );
}

/* Custom Judge Master section with "View Profile" button */
function JudgeMasterSection({ courtOptions }) {
  const navigate = useNavigate();
  const endpoint = '/masters/judges/';
  const fields = [
    { key: 'name', label: 'Judge Name', required: true, wide: true },
    { key: 'designation', label: 'Designation', required: false },
    { key: 'court', label: 'Court Name', type: 'select', options: courtOptions, required: false },
  ];

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [search, setSearch] = useState('');

  const load = async () => {
    try { const { data } = await api.get(endpoint); setItems(data.results || data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const setFormField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setEditField = (k, v) => setEditData(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post(endpoint, form); toast.success('Added!'); setForm({}); setShowForm(false); load(); }
    catch { toast.error('Failed to add'); }
    setLoading(false);
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this judge?')) return;
    try { await api.delete(`${endpoint}${id}/`); toast.success('Deleted!'); load(); } catch { toast.error('Failed'); }
  };
  const startEdit = (item) => { setEditId(item.id); setEditData({ ...item }); };
  const cancelEdit = () => { setEditId(null); setEditData({}); };
  const saveEdit = async (id) => {
    try { await api.patch(`${endpoint}${id}/`, editData); toast.success('Updated!'); setEditId(null); load(); }
    catch { toast.error('Failed to update'); }
  };

  const inlineFields = fields.filter(f => f.type !== 'textarea');
  const filtered = search.trim()
    ? items.filter(item =>
        (item.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (item.designation || '').toLowerCase().includes(search.toLowerCase()) ||
        (item.court_name || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Scale size={18} className="text-blue-900" />
          <h2 className="font-semibold text-gray-800">Judge Master</h2>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {search.trim() ? `${filtered.length} / ${items.length}` : items.length}
          </span>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-sm text-blue-900 hover:text-blue-700 font-medium">
          <Plus size={15} /> Add New
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search judges…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-blue-50 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            {inlineFields.map(f => (
              <div key={f.key} className={f.wide ? 'flex-1 min-w-[200px]' : 'w-44'}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <FieldInput f={f} value={form[f.key]} onChange={setFormField} />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={loading} className="bg-blue-950 text-white px-4 py-2 rounded-xl text-sm font-medium">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="border px-4 py-2 rounded-xl text-sm text-gray-600">Cancel</button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="divide-y divide-gray-50 overflow-y-auto max-h-[560px]">
        {filtered.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">
            {search.trim() ? 'No results found' : 'No judges yet'}
          </p>
        ) : filtered.map(item => (
          <div key={item.id} className="px-5 py-3">
            {editId === item.id ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {inlineFields.map(f => (
                    <div key={f.key} className={f.wide ? 'flex-1 min-w-[200px]' : 'w-44'}>
                      <FieldInput f={f} value={editData[f.key]} onChange={setEditField} />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(item.id)} className="flex items-center gap-1 text-xs bg-blue-950 text-white px-3 py-1.5 rounded-lg"><Check size={12} /> Save</button>
                  <button onClick={cancelEdit} className="flex items-center gap-1 text-xs border px-3 py-1.5 rounded-lg text-gray-600"><X size={12} /> Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{item.name}</p>
                  {item.designation && <p className="text-xs text-blue-700 font-medium">{item.designation}</p>}
                  {item.court_name && <p className="text-xs text-gray-500">📍 {item.court_name}</p>}
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => navigate(`/masters/judges/${item.id}/profile?mode=view`)}
                      className="inline-flex items-center gap-1 text-xs text-blue-900 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2.5 py-1 rounded-lg font-medium transition-colors"
                    >
                      <Eye size={11} /> View Profile
                    </button>
                    <button
                      onClick={() => navigate(`/masters/judges/${item.id}/profile?mode=edit`)}
                      className="inline-flex items-center gap-1 text-xs text-white bg-blue-900 hover:bg-blue-700 px-2.5 py-1 rounded-lg font-medium transition-colors"
                    >
                      <PenLine size={11} /> Edit Profile
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(item)} className="text-gray-300 hover:text-blue-500 transition-colors"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MastersPage() {
  const [courts, setCourts] = useState([]);

  useEffect(() => {
    api.get('/masters/courts/').then(r => setCourts(r.data.results || r.data)).catch(() => {});
  }, []);

  const courtOptions = courts.map(c => ({ value: c.id, label: c.name }));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Masters</h1>
        <p className="text-sm text-gray-500">Manage lookup data for the system</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MasterSection
          title="Court List"
          icon={Building2}
          endpoint="/masters/courts/"
          fields={[
            { key: 'name', label: 'Court Name', required: true, wide: true },
            { key: 'location', label: 'City / Area', required: false },
            { key: 'address', label: 'Full Address', type: 'textarea', placeholder: 'e.g. District Court, Shivaji Nagar, Pune, Maharashtra 411005', required: false },
          ]}
        />
        <JudgeMasterSection courtOptions={courtOptions} />
        <MasterSection
          title="Case Stages"
          icon={BookOpen}
          endpoint="/masters/case-stages/"
          fields={[
            { key: 'name', label: 'Stage Name', required: true, wide: true },
          ]}
        />
      </div>
    </div>
  );
}
